import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Root,
  Hero,
  HeroImageContainer,
  HeroContent,
  HeroClubLogoContainer,
  StrategiesLogo,
  DescSection,
  DescTitle,
  DescSubtitle,
  Paragraph,
  AboutTitle,
  CardsSection,
  TopCardsGridContainer,
  TopCardsGrid,
  TopCard,
  BottomCardsGridContainer,
  BottomCardsGrid,
} from './CoachingPackages.style';

import ClubHLogo from '../../../assets/clubh-logo.svg?react';
import logoStrategiesSrc from '../../../assets/logo-strategies@2x.png';

import inPersonEventsSrc from '../../../assets/images/education/in_person_events.jpg';

import topCard1 from '../../../assets/images/education/01_live_coaching_calls.jpg';
import topCard2 from '../../../assets/images/education/02_data_driven_clarity.jpg';
import topCard3 from '../../../assets/images/education/03_actionable_strategies.jpg';

import { TwoColumnSection } from '../../../components/TwoColumnSection/TwoColumnSection';
import { Button } from '../../../ui/Button/Button';
import { Link } from '../../../components/Link/Link';
import { ProgramFocusCard } from '../../../components/ProgramFocusCard/ProgramFocusCard';
import { StrategiesBanner } from '../../../components/StrategiesBanner/StrategiesBanner';
import { EducationSuccessStories } from '../../../components/EducationSuccessStories/EducationSuccessStories';
import { ReadyToStart } from '../../../components/ReadyToStart/ReadyToStart';

export const CoachingPackages = () => {
  const intl = useIntl();

  return (
    <Root>
      <Hero>
        <HeroImageContainer />
        <HeroContent>
          <HeroClubLogoContainer>
            <ClubHLogo />
          </HeroClubLogoContainer>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="31px"
            height="31px"
            viewBox="0 0 31 31"
            style={{ flexShrink: 0 }}
          >
            <path
              fill="#fff"
              d="m29.64 0 1.337 1.334-14.142 14.165L31 29.64l-1.334 1.336-14.165-14.141L1.36 31 .024 29.666l14.141-14.165L0 1.36 1.333.024l14.166 14.141L29.64 0Z"
            />
          </svg>
          <StrategiesLogo
            src={logoStrategiesSrc}
            alt={intl.formatMessage({ defaultMessage: 'Strategies Logo' })}
          />
        </HeroContent>
      </Hero>

      <DescSection>
        <DescTitle>
          <FormattedMessage defaultMessage="Coaching packages" />
        </DescTitle>
        <DescSubtitle>
          <FormattedMessage defaultMessage="Unlock Your Business’s Hidden Growth Potential with Strategies" />
        </DescSubtitle>
        <p>
          <Paragraph>
            <FormattedMessage defaultMessage="The Limitless Growth Coaching Sprint is a focused, 6-session coaching program designed to help salon and spa owners get unstuck and accelerate financial growth." />
          </Paragraph>
        </p>
        <p>
          <Paragraph style={{ fontSize: '29px' }}>
            <strong>
              <FormattedMessage defaultMessage="1,350,000 PTS" />
            </strong>
          </Paragraph>
        </p>
        <Button
          as={Link}
          to="/rewards/business-education-coaching-packages"
          style={{ backgroundColor: '#e74391' }}
        >
          <FormattedMessage defaultMessage="REDEEM NOW" />
        </Button>
        <span>
          <DescSubtitle>
            <FormattedMessage defaultMessage="Not enough points?" />
          </DescSubtitle>
          <p>
            <Paragraph>
              <FormattedMessage defaultMessage="Reach out to your sales representative for more information" />
            </Paragraph>
          </p>
        </span>
      </DescSection>

      <AboutTitle>
        <FormattedMessage defaultMessage="ABOUT THE PROGRAM" />
      </AboutTitle>

      <CardsSection>
        <DescTitle>
          <FormattedMessage defaultMessage="What you’ll get" />
        </DescTitle>
        <TopCardsGridContainer>
          <TopCardsGrid>
            <TopCard>
              <img
                src={topCard1}
                alt={intl.formatMessage({
                  defaultMessage:
                    '6 live coaching calls with Strategies coaches',
                })}
              />
              <DescSubtitle>
                <FormattedMessage defaultMessage="6 live coaching calls with Strategies coaches" />
              </DescSubtitle>
            </TopCard>
            <TopCard>
              <img
                src={topCard2}
                alt={intl.formatMessage({
                  defaultMessage:
                    'Data-driven clarity on your business metrics',
                })}
              />
              <DescSubtitle>
                <FormattedMessage defaultMessage="Data-driven clarity on your business metrics" />
              </DescSubtitle>
            </TopCard>
            <TopCard>
              <img
                src={topCard3}
                alt={intl.formatMessage({
                  defaultMessage:
                    'Actionable strategies you can implement right away',
                })}
              />
              <DescSubtitle>
                <FormattedMessage defaultMessage="Actionable strategies you can implement right away" />
              </DescSubtitle>
            </TopCard>
          </TopCardsGrid>
        </TopCardsGridContainer>
        <DescTitle>
          <FormattedMessage defaultMessage="What we focus on" />
        </DescTitle>
        <BottomCardsGridContainer>
          <BottomCardsGrid>
            <ProgramFocusCard
              title={intl.formatMessage({
                defaultMessage: 'Data-Driven Clarity',
              })}
              desc={intl.formatMessage({
                defaultMessage: 'Know exactly which metrics drive your growth',
              })}
            />
            <ProgramFocusCard
              title={intl.formatMessage({
                defaultMessage: 'New Client Acquisition',
              })}
              desc={intl.formatMessage({
                defaultMessage:
                  'Build a steady pipeline of first-time customers',
              })}
            />
            <ProgramFocusCard
              title={intl.formatMessage({
                defaultMessage: 'Retention Systems',
              })}
              desc={intl.formatMessage({
                defaultMessage: 'Keep your existing clients coming back',
              })}
            />
            <ProgramFocusCard
              title={intl.formatMessage({
                defaultMessage: 'Operational Systems',
              })}
              desc={intl.formatMessage({
                defaultMessage:
                  'Streamline processes that boost profit and efficiency',
              })}
            />
            <ProgramFocusCard
              title={intl.formatMessage({
                defaultMessage: 'Big Picture Strategy',
              })}
              desc={intl.formatMessage({
                defaultMessage:
                  'Set next steps for sustainable business success',
              })}
            />
          </BottomCardsGrid>
        </BottomCardsGridContainer>
      </CardsSection>
      <TwoColumnSection
        imageSrc={inPersonEventsSrc}
        title={intl.formatMessage({
          defaultMessage: 'Why Join the Limitless Growth Coaching Sprint?',
        })}
        desc={
          <span>
            <p>
              <FormattedMessage defaultMessage="You’ve built a successful salon or spa, but economic changes, burnout, and uncertainty can make it feel like your momentum has slowed and real growth has stalled." />
            </p>
            <br />
            <p style={{ marginBottom: 6, fontSize: 20 }}>
              <strong>
                <FormattedMessage defaultMessage="This coaching sprint will" />
              </strong>
            </p>
            <TwoColumnSection.List>
              <li>
                <FormattedMessage defaultMessage="Help you uncover opportunities you may be missing" />
              </li>
              <li>
                <FormattedMessage defaultMessage="Create systems that fuel growth and retention" />
              </li>
              <li>
                <FormattedMessage defaultMessage="Get you back in control of your numbers and future" />
              </li>
            </TwoColumnSection.List>
          </span>
        }
        accentColor="#e74391"
      />

      <StrategiesBanner />
      <EducationSuccessStories />
      <ReadyToStart />
    </Root>
  );
};
